import { useQuery } from '@apollo/client';
import { Arrange, Button, Container, Spacer, Text } from '@loomhq/lens';
import pluralize from 'pluralize';
import React from 'react';
import { useHover, useLayer } from 'react-laag';
import { useSelector } from 'react-redux';

import { PROFILE_HOVERED } from 'constants/events';
import { selectCurrentUser } from 'selectors/user';
import { track } from 'utilities/analytics';

import useDestination from 'hooks/useDestination';
import { useGetSelectedWorkspace } from 'hooks/workspace';

import GetUserWithProfileInfo from './GetUserWithProfileInfo.graphql';

import FollowButton from 'components/follow-button';
import UserAvatar from 'components/user-avatar';

import styles from './styles.module.less';

export default function ProfileCard({
  avatarSrc,
  hasFocus,
  name,
  useLoomSSRVideo,
  profileId,
  children,
  cachedUserProfileData = null,
  placement = 'right-center',
}) {
  const [isOver, hoverProps] = useHover({
    delayEnter: 400,
    delayLeave: 200,
    hideOnScroll: true,
  });
  const { triggerProps, layerProps, renderLayer } = useLayer({
    isOpen: isOver,
    ResizeObserver,
    placement,
    auto: true,
  });

  const currentUser = useSelector(selectCurrentUser);
  const selectedWorkspace = useGetSelectedWorkspace();
  const { loading: userInfoLoading, data: userProfileData } = useQuery(
    GetUserWithProfileInfo,
    {
      variables: {
        userId: profileId,
      },
      fetchPolicy: 'no-cache',
      skip: useLoomSSRVideo || cachedUserProfileData || !profileId,
    }
  );
  const showDestination = useDestination();

  let profile;
  let profileUrl;
  let profileVideoCount;

  if (useLoomSSRVideo) {
    const { ownerProfile, ownerProfileVideoCount, owner_id, ownerProfileUrl } =
      window.loomSSRVideo;

    profileId = owner_id;

    profile = ownerProfile;
    profileUrl = ownerProfileUrl;
    profileVideoCount = ownerProfileVideoCount;
  } else {
    const user = (cachedUserProfileData || userProfileData)?.user?.user;

    if (!user) {
      return children;
    }

    profile = user?.profile?.profileInfo;
    profileUrl = user?.profile?.profileUrl;
    profileVideoCount = user?.profile?.profileVideoCount;
  }

  const currentUserIsOwner = profileId == currentUser?.id;

  if ((!useLoomSSRVideo && userInfoLoading) || !profile) {
    return children;
  }

  if (isOver) {
    track(PROFILE_HOVERED, {
      user_id: currentUser.id,
      organization_id: selectedWorkspace.id,
      context_page_path: window.location.pathname,
    });
  }

  let showCtas;

  if (
    !useLoomSSRVideo ||
    window.loomSSRVideo?.organization_id == selectedWorkspace.id
  ) {
    showCtas = (
      <>
        <Spacer top="medium" />
        <Arrange gap="small" columns={currentUserIsOwner ? '1fr' : '1fr 1fr'}>
          {!currentUserIsOwner && (
            <FollowButton profileId={profileId} hasFullWidth />
          )}
          <Button htmlTag="a" variant="neutral" href={`/profile/${profileUrl}`}>
            View profile
          </Button>
        </Arrange>
      </>
    );
  }

  return (
    <>
      {showDestination === false && children}
      {showDestination === true && (
        <>
          <div {...triggerProps} {...hoverProps}>
            <ProfileLink profileUrl={profileUrl} hasFocus={hasFocus}>
              {children}
            </ProfileLink>
          </div>
          {isOver &&
            renderLayer(
              <div
                className={styles.profileCard}
                {...layerProps}
                {...hoverProps}
              >
                <Spacer all="xsmall">
                  <Container
                    backgroundColor="background"
                    borderSide="all"
                    width="41"
                    padding="medium"
                    radius="medium"
                    shadow="medium"
                  >
                    <Arrange alignItems="start" gap="medium">
                      <ProfileLink hasFocus={false} profileUrl={profileUrl}>
                        <UserAvatar
                          avatarSrc={avatarSrc}
                          name={name}
                          avatarSize={8}
                        />
                      </ProfileLink>
                      <div>
                        <ProfileLink hasFocus={true} profileUrl={profileUrl}>
                          <Text fontWeight="medium" size="large">
                            {name}
                          </Text>
                        </ProfileLink>
                        <Spacer top={0.25} />
                        <div className={styles.breakWords}>
                          <Text htmlTag="p" color="bodyDimmed">
                            {profile?.role && <>{profile?.role}</>}
                            {profile?.role && profile?.location && ' · '}
                            {profile?.location && <>{profile?.location}</>}
                          </Text>
                        </div>
                        <Spacer top="small" />
                        {profileVideoCount === 0 ? (
                          <Text fontWeight="medium">Zero published videos</Text>
                        ) : (
                          <Text fontWeight="medium">
                            {profileVideoCount}{' '}
                            {pluralize('published video', profileVideoCount)}
                          </Text>
                        )}
                      </div>
                    </Arrange>
                    {showCtas}
                  </Container>
                </Spacer>
              </div>
            )}
        </>
      )}
    </>
  );
}

function ProfileLink({ hasFocus, profileUrl, children }) {
  const avatarMode = children.type?.name === 'UserAvatar';

  return (
    <a
      aria-hidden={hasFocus ? false : true}
      className={avatarMode ? styles.avatarLink : styles.textLink}
      href={`/profile/${profileUrl}`}
      tabIndex={hasFocus ? 0 : -1}
    >
      {children}
    </a>
  );
}
